import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    type UseFormGetValues,
    type UseFormSetValue,
    type UseFormHandleSubmit,
    FormProvider,
    useForm,
    yupResolver,
} from 'FrontRoyalReactHookForm';
import { useEffect, useMemo, useRef, type KeyboardEvent, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { faCommentLines } from '@fortawesome/pro-thin-svg-icons';
import { faCircleStop, faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { useTheme } from 'Theme';
import { AutoGrowTextAreaField } from 'AutoGrowTextAreaField';
import Capabilities from 'Capabilities';
import { twMerge } from 'tailwind-merge';
import { useChatContext } from '../Chat/ChatContext';
import { type MessageFormValues } from '../types/OtherInternalTypes.types';
import { type TutorBotChatStatusConfig } from '../types/TutorBotChatStatusConfig.types';

type Props = {
    onSubmit: (data: MessageFormValues) => void;
    onCancelClick: () => void;
    className?: string;
    maxLength?: number;
    disableInput?: boolean;
    canCancel?: boolean;
    hideCharacterCount?: boolean;
    onMessageChange?: (message: string) => void;
};

function useInputFocus({ disableInput }: Pick<Props, 'disableInput'>) {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const { isMobile } = useIsMobileMixin();

    useEffect(() => {
        if (isMobile || window.CORDOVA) return;

        if (!disableInput && inputRef.current) {
            inputRef.current.focus();
        }
    }, [disableInput, isMobile]);

    return inputRef;
}

function useMessageForm({ maxLength }: { maxLength: number }) {
    const formValidationSchema = Yup.object()
        .shape({
            message: Yup.string().trim().required().max(maxLength),
        })
        .required();
    const formFunctions = useForm<MessageFormValues>({
        resolver: yupResolver(formValidationSchema),
        defaultValues: { message: '' },
        mode: 'all',
    });

    const message = formFunctions.watch('message');
    const errors = formFunctions.formState.errors;
    const messageInvalid = !!errors?.message;
    const messageTooLong = messageInvalid && errors.message!.type === 'max';

    return {
        formFunctions,
        handleSubmit: formFunctions.handleSubmit,
        getValues: formFunctions.getValues,
        setFieldValue: formFunctions.setFieldValue,
        hasMessage: !!message?.length,
        messageInvalid,
        messageTooLong,
        message,
    };
}

function useInputLabel({ tutorBotStatusConfig }: { tutorBotStatusConfig: TutorBotChatStatusConfig }) {
    const { t } = useTranslation('back_royal');

    const inputLabel = useMemo(() => {
        const inputLabelLocale = tutorBotStatusConfig.inputLabel;
        const key = inputLabelLocale?.key;
        const params = inputLabelLocale?.params;
        return key ? t(key, params || {}) : '';
    }, [t, tutorBotStatusConfig]);

    return inputLabel;
}

function useInputButtonClasses() {
    const { bgColor } = useTheme();
    const colorClass =
        bgColor === 'turquoise'
            ? 'text-turquoise'
            : bgColor === 'purple'
              ? 'text-purple'
              : bgColor === 'blue'
                ? 'text-blue'
                : 'quantic:text-coral valar:text-blue';
    return clsx(
        'm-0 block border-none bg-transparent p-0 transition-all hover:brightness-90 disabled:cursor-not-allowed disabled:opacity-70',
        colorClass,
    );
}

function CharacterCount({
    hasMessage,
    messageTooLong,
    message,
    maxLength,
    hide = false,
}: {
    hasMessage: boolean;
    messageTooLong: boolean;
    message: string;
    maxLength: Props['maxLength'];
    hide?: boolean;
}) {
    return (
        <div
            data-testid="character-count"
            className={twMerge(
                'absolute -bottom-[26px] text-xs ltr:right-5 rtl:left-5',
                messageTooLong ? 'text-red' : 'text-slate-grey',
                // hidden on mobile to avoid overlap or if the hide prop is true
                hide ? 'hidden' : 'hidden sm:block',
            )}
        >
            {hasMessage && (
                <>
                    <span className="rtl:hidden">
                        {message.length} / {maxLength}
                    </span>
                    <span className="ltr:hidden">
                        {maxLength} / {message.length}
                    </span>
                </>
            )}
        </div>
    );
}

function FormButtons({
    hasMessage,
    isLocked,
    messageInvalid,
    disableInput,
    canCancel,
    onCancelClick,
}: Pick<Props, 'disableInput' | 'canCancel' | 'onCancelClick'> & {
    hasMessage: boolean;
    messageInvalid: boolean;
    isLocked: boolean;
}) {
    const { t } = useTranslation('back_royal');
    const inputButtonClasses = useInputButtonClasses();

    return (
        <>
            {hasMessage && (
                <button
                    data-testid="submit"
                    className={inputButtonClasses}
                    type="submit"
                    disabled={isLocked || messageInvalid || disableInput}
                >
                    <FontAwesomeIcon
                        className="block text-xl rtl:-scale-x-100"
                        fontStyle="solid"
                        icon={faPaperPlaneTop}
                    />
                    <span className="sr-only">{t('chat.chat.submit_message')}</span>
                </button>
            )}
            {canCancel && (
                <button
                    data-testid="stop"
                    onClick={onCancelClick}
                    className={inputButtonClasses}
                    type="button"
                    title={t('chat.chat.cancel_response')!}
                >
                    <FontAwesomeIcon className="block text-xl" fontStyle="solid" icon={faCircleStop} />
                    <span className="sr-only">{t('chat.chat.cancel_response')}</span>
                </button>
            )}
        </>
    );
}

// This hook makes it so that clicking anywhere inside the form focuses the input, even if you click
// around the edges and miss the invisible textarea.
function useFocusInputOnContainerClick({
    disableInput,
    inputRef,
}: {
    disableInput: boolean;
    inputRef: MutableRefObject<HTMLTextAreaElement | null>;
}) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function focusInput(event: MouseEvent | TouchEvent) {
            if (disableInput || !inputRef.current) {
                return;
            }

            // Clicking directly on the textarea already yields focus, so avoid redundant work.
            const target = event.target as HTMLElement | null;
            if (target && inputRef.current.contains(target)) {
                return;
            }

            // Delay the focus until after the current pointer interaction finishes so nothing immediately steals it back.
            if (event.type === 'mousedown') {
                event.preventDefault();
            }
            window.requestAnimationFrame(() => {
                inputRef.current?.focus({ preventScroll: true });
            });
        }

        const node = containerRef.current;
        if (node) {
            node.addEventListener('mousedown', focusInput);
            node.addEventListener('touchstart', focusInput);
        }
        return () => {
            if (node) {
                node.removeEventListener('mousedown', focusInput);
                node.removeEventListener('touchstart', focusInput);
            }
        };
    }, [disableInput, inputRef]);

    return containerRef;
}

function MessageFormInternal({
    handleSubmit,
    onSubmit,
    setFieldValue,
    disableInput,
    tutorBotStatusConfig,
    getValues,
    hasMessage,
    messageInvalid,
    canCancel,
    onCancelClick,
    inputRef,
}: Pick<Props, 'onSubmit' | 'disableInput' | 'onCancelClick' | 'canCancel'> & {
    tutorBotStatusConfig: TutorBotChatStatusConfig;
    handleSubmit: UseFormHandleSubmit<MessageFormValues>;
    setFieldValue: UseFormSetValue<MessageFormValues>;
    getValues: UseFormGetValues<MessageFormValues>;
    hasMessage: boolean;
    messageInvalid: boolean;
    inputRef: MutableRefObject<HTMLTextAreaElement | null>;
}) {
    const onSubmitInner = (data: MessageFormValues) => {
        onSubmit({ message: data.message.trim() });
        setFieldValue('message', '');
    };
    // Enter sends only on a device with a physical keyboard, where Shift+Enter inserts a line break.
    // An on-screen keyboard has no usable Shift key, so Enter inserts a line break there and the
    // send button is the only way to send. See https://trello.com/c/kQ8oGDV7
    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key !== 'Enter' || e.shiftKey || Capabilities.touchPrimary) return;
        if (disableInput || messageInvalid) return;

        e.preventDefault();
        onSubmitInner(getValues());
    };
    const inputLabel = useInputLabel({ tutorBotStatusConfig });

    return (
        <form
            data-testid="message-form"
            className="flex h-full rounded-12 border border-slate-grey-200 bg-white px-4 py-3 text-black shadow-smallish"
            onSubmit={handleSubmit(onSubmitInner)}
        >
            <div className={clsx('grow-0 basis-6 text-slate-grey', { 'opacity-40': disableInput })}>
                <FontAwesomeIcon className="block text-xl" icon={faCommentLines} />
            </div>
            <div className="flex flex-1 items-stretch px-4">
                <AutoGrowTextAreaField
                    data-testid="message-input"
                    ref={inputRef}
                    name="message"
                    aria-label={inputLabel}
                    className="h-full max-h-[400px] w-full flex-1 overflow-y-auto bg-transparent placeholder-slate-grey outline-none scrollbar-hide disabled:cursor-not-allowed disabled:placeholder-gray-300"
                    wrapperClassName="h-full w-full"
                    placeholder={inputLabel}
                    onKeyDown={handleKeyDown}
                    disabled={disableInput}
                />
            </div>
            <div className="grow-0 basis-6">
                <FormButtons
                    hasMessage={hasMessage}
                    isLocked={tutorBotStatusConfig.isLocked}
                    messageInvalid={messageInvalid}
                    disableInput={disableInput}
                    canCancel={canCancel}
                    onCancelClick={onCancelClick}
                />
            </div>
        </form>
    );
}

export function MessageForm({
    onSubmit,
    onCancelClick,
    className,
    maxLength = 2000,
    disableInput = false,
    canCancel = false,
    hideCharacterCount = false,
    onMessageChange,
}: Props) {
    const { tutorBotStatusConfig } = useChatContext();

    const {
        handleSubmit,
        getValues,
        setFieldValue,
        formFunctions,
        message,
        hasMessage,
        messageInvalid,
        messageTooLong,
    } = useMessageForm({ maxLength });
    const inputRef = useInputFocus({ disableInput });
    const containerRef = useFocusInputOnContainerClick({
        disableInput: Boolean(disableInput),
        inputRef,
    });

    useEffect(() => {
        onMessageChange?.(message || '');
    }, [message, onMessageChange]);

    return (
        <FormProvider {...formFunctions}>
            <div
                data-testid="message-form-container"
                className={clsx('relative', 'text-base', className)}
                ref={containerRef}
            >
                <MessageFormInternal
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    setFieldValue={setFieldValue}
                    disableInput={disableInput}
                    tutorBotStatusConfig={tutorBotStatusConfig}
                    getValues={getValues}
                    canCancel={canCancel}
                    onCancelClick={onCancelClick}
                    hasMessage={hasMessage}
                    messageInvalid={messageInvalid}
                    inputRef={inputRef}
                />
                <CharacterCount
                    hasMessage={hasMessage}
                    messageTooLong={messageTooLong}
                    message={message}
                    maxLength={maxLength}
                    hide={hideCharacterCount}
                />
            </div>
        </FormProvider>
    );
}
